/** @type {import('../schema').BuilderLesson} */
const breadboardBasicsLesson = {
  id: 'breadboard-basics',
  version: 1,
  title: 'How to Use a Breadboard',
  description: 'Learn how breadboard rows, power rails, jumper wires, LEDs, and resistors form electrical nodes.',
  estimatedMinutes: 12,
  availableComponents: ['breadboard', 'dc_power', 'led', 'resistor'],
  componentLimits: {
    led: 1,
    resistor: 1,
  },
  lockedComponentIds: ['main-breadboard', 'lesson-5v-source'],
  workspace: {
    fitToContentOnStart: true,
    initialZoom: 1.2,
    minimumZoom: 0.45,
    maximumZoom: 2.5,
  },
  initialProject: {
    canvas: {
      zoomLevel: 1.2,
      showGrid: false,
      snapToGrid: false,
      gridSize: 10,
    },
    components: [
      {
        id: 'main-breadboard',
        passiveType: 'breadboard',
        passiveKey: 'breadboard',
        name: 'Main Breadboard',
        x: 330,
        y: 265,
        scale: 1.45,
        rotation: 0,
        locked: true,
      },
      {
        id: 'lesson-5v-source',
        passiveType: 'dc_power',
        passiveKey: 'dc_power',
        name: '5 V Source',
        x: 105,
        y: 275,
        scale: 1,
        rotation: 0,
        locked: true,
      },
    ],
    connections: [],
  },
  steps: [
    {
      id: 'explore-terminal',
      title: 'Explore the breadboard',
      instruction: 'Click one of the outlined holes in the main terminal area.',
      explanation: 'The main terminal area is the grid of holes on both sides of the center trench.',
      validation: {
        type: 'selection',
        target: 'terminal-hole',
      },
      allowedActions: {
        selectBreadboardHoles: true,
        addWires: false,
        addComponents: false,
      },
      highlights: [
        {
          type: 'breadboard-group',
          breadboardId: 'main-breadboard',
          pinKeys: ['pin10A', 'pin10B', 'pin10C', 'pin10D', 'pin10E'],
        },
      ],
      hints: [
        { text: 'Choose one of the outlined holes, away from the red and blue power rails.' },
        {
          text: 'Try any hole near the middle of the breadboard.',
          highlight: {
            type: 'breadboard-group',
            breadboardId: 'main-breadboard',
            pinKeys: ['pin10A', 'pin10B', 'pin10C', 'pin10D', 'pin10E'],
          },
        },
      ],
      successMessage: 'These five holes are connected by a metal strip underneath the breadboard.',
    },
    {
      id: 'compare-center-trench',
      title: 'Compare both sides of the center trench',
      instruction: 'Click a hole with the same number as your last choice, but on the other side of the center trench.',
      explanation: 'The center trench lets DIP chips straddle the gap without shorting both sides together.',
      validation: {
        type: 'selection',
        target: 'opposite-terminal-half',
        referenceSelectionId: 'explore-terminal',
      },
      allowedActions: {
        selectBreadboardHoles: true,
        addWires: false,
        addComponents: false,
      },
      highlights: [
        {
          type: 'selection-reference',
          selectionId: 'explore-terminal',
        },
        {
          type: 'opposite-terminal-group',
          selectionId: 'explore-terminal',
        },
      ],
      hints: [
        { text: 'Stay in the same numbered column, then cross the gap to the other half.' },
        { text: 'If the first hole was in A-E, choose F-J in the same column. If it was in F-J, choose A-E.' },
      ],
      successMessage: 'The center trench separates the two groups. The holes across the gap are not internally connected.',
    },
    {
      id: 'explore-positive-rail',
      title: 'Explore a power rail',
      instruction: 'Click a hole in the positive power rail.',
      explanation: 'Power rails usually run along the side of the breadboard. Some breadboards divide a rail into separate sections.',
      validation: {
        type: 'selection',
        target: 'positive-rail-hole',
      },
      allowedActions: {
        selectBreadboardHoles: true,
        addWires: false,
        addComponents: false,
      },
      highlights: [
        {
          type: 'power-rail',
          breadboardId: 'main-breadboard',
          rail: 'positive',
        },
      ],
      hints: [
        { text: 'Look for a red rail along the top or bottom edge.' },
        { text: 'The positive rail holes are marked as the red rows in this builder.' },
      ],
      successMessage: 'That rail segment is one shared electrical node.',
    },
    {
      id: 'connect-power',
      title: 'Connect the supply',
      instruction: 'Connect the positive terminal of the power source to the positive rail and the negative terminal to the ground rail.',
      explanation: 'Use jumper wires from the 5 V source to the rails. Keep the red positive rail separate from the blue ground rail.',
      validation: {
        type: 'power-connected',
      },
      allowedActions: {
        addWires: true,
        deleteWires: true,
        selectBreadboardHoles: false,
        addComponents: false,
      },
      highlights: [
        {
          type: 'component-terminal',
          componentId: 'lesson-5v-source',
          pinKey: '+',
        },
        {
          type: 'component-terminal',
          componentId: 'lesson-5v-source',
          pinKey: '-',
        },
        {
          type: 'power-rail',
          breadboardId: 'main-breadboard',
          rail: 'positive',
        },
        {
          type: 'power-rail',
          breadboardId: 'main-breadboard',
          rail: 'ground',
        },
      ],
      hints: [
        { text: 'Click the + terminal, then click a hole on a red rail. Repeat from - to a blue rail.' },
        { text: 'Use two separate wires: one for 5 V and one for ground.' },
      ],
      correctionMessages: [
        {
          code: 'power-short',
          message: 'Positive and ground are connected together. Remove the short circuit before continuing.',
        },
        {
          code: 'positive-rail-missing',
          message: 'The positive terminal still needs a wire to a red power rail.',
        },
        {
          code: 'ground-rail-missing',
          message: 'The negative terminal still needs a wire to a blue ground rail.',
        },
      ],
      successMessage: 'Your breadboard now has a 5 V rail and a ground rail.',
    },
    {
      id: 'place-led',
      title: 'Place an LED correctly',
      instruction: 'Place the LED so its two legs are in different terminal groups.',
      explanation: 'The LED does not need to be powered yet. The important part is that the two legs do not share the same breadboard strip.',
      validation: {
        type: 'all',
        rules: [
          {
            type: 'component-exists',
            componentType: 'led',
          },
          {
            type: 'component-terminal-in-hole',
            componentType: 'led',
            terminalKey: 'A',
          },
          {
            type: 'component-terminal-in-hole',
            componentType: 'led',
            terminalKey: 'K',
          },
          {
            type: 'component-terminals-in-different-nets',
            componentType: 'led',
            terminalKeys: ['A', 'K'],
          },
        ],
      },
      allowedActions: {
        addComponents: true,
        moveComponents: true,
        deleteComponents: true,
        addWires: false,
      },
      availableComponents: ['led'],
      highlights: [
        {
          type: 'component-tray',
          componentKey: 'led',
        },
      ],
      hints: [
        { text: 'Add an LED, then drag it until both legs snap into breadboard holes.' },
        { text: 'If both legs land in the same five-hole strip, move one leg to a different row.' },
      ],
      correctionMessages: [
        {
          code: 'terminals-same-net',
          message: 'Both LED legs are currently connected to the same breadboard strip. Move one leg to a different row.',
        },
      ],
      successMessage: 'Good LED placement. Its two legs are on separate electrical nodes.',
    },
    {
      id: 'add-resistor',
      title: 'Add the current-limiting resistor',
      instruction: 'Place a resistor in series with the LED.',
      explanation: 'A series resistor shares one node with the LED, while its other lead goes to a different node.',
      validation: {
        type: 'resistor-in-series-with-led',
      },
      allowedActions: {
        addComponents: true,
        moveComponents: true,
        deleteComponents: true,
        addWires: true,
      },
      availableComponents: ['resistor'],
      highlights: [
        {
          type: 'component-tray',
          componentKey: 'resistor',
        },
        {
          type: 'component',
          componentType: 'led',
        },
      ],
      hints: [
        { text: 'Add a resistor and snap one lead into the same row as one LED leg.' },
        { text: 'The other resistor lead should land in a different row so current must pass through the resistor.' },
      ],
      correctionMessages: [
        {
          code: 'resistor-same-net',
          message: 'The resistor leads are in the same connected row, so it is not in series with the LED.',
        },
      ],
      successMessage: 'The resistor is now in series with the LED.',
    },
    {
      id: 'complete-led-circuit',
      title: 'Complete the LED circuit',
      instruction: 'Use jumper wires to complete a path from positive supply, through the resistor and LED, and back to ground.',
      explanation: 'The resistor may be on either side of the LED. The LED anode must face the positive side and the cathode must face ground.',
      validation: {
        type: 'led-circuit',
      },
      allowedActions: {
        addWires: true,
        deleteWires: true,
        moveComponents: true,
      },
      highlights: [
        {
          type: 'power-rail',
          breadboardId: 'main-breadboard',
          rail: 'positive',
        },
        {
          type: 'power-rail',
          breadboardId: 'main-breadboard',
          rail: 'ground',
        },
        {
          type: 'component',
          componentType: 'led',
        },
        {
          type: 'component',
          componentType: 'resistor',
        },
      ],
      hints: [
        { text: 'A valid path is 5 V, resistor, LED anode, LED cathode, ground.' },
        { text: 'It is also valid to put the resistor between the LED cathode and ground.' },
      ],
      correctionMessages: [
        { code: 'led-reversed', message: 'LED polarity is reversed. Put the anode toward 5 V and the cathode toward ground.' },
        { code: 'open-circuit', message: 'The path is still open. Check for a missing jumper or a lead in the wrong row.' },
        { code: 'missing-ground', message: 'The circuit does not return to ground yet.' },
        { code: 'resistor-bypassed', message: 'The resistor is bypassed. Current must pass through it, not around it.' },
        { code: 'led-same-net', message: 'Both LED legs are in one net. Move one LED leg to a different strip.' },
        { code: 'direct-short', message: 'Positive and ground are shorted together. Remove that direct connection.' },
      ],
      successMessage: 'The topology is valid. The LED would be on in this lesson model.',
    },
    {
      id: 'trace-review',
      title: 'Trace and review',
      instruction: 'Click Trace Connections, then select different points in the circuit to inspect their electrical nets.',
      explanation: 'Inspect the positive rail, the ground rail, and the node between the resistor and LED.',
      validation: {
        type: 'trace-inspected',
        requireEnabled: true,
        targets: ['positive', 'ground', 'resistor-led-junction'],
      },
      allowedActions: {
        selectBreadboardHoles: true,
        traceConnections: true,
      },
      highlights: [
        {
          type: 'trace-tool',
        },
      ],
      hints: [
        { text: 'Turn on Trace Connections in the tutorial panel.' },
        { text: 'Click a red rail, a blue rail, and the row shared by the resistor and LED.' },
      ],
      successMessage: 'A breadboard does not connect every nearby hole. Components work when their leads and jumper wires form the intended electrical nodes and a complete circuit path.',
    },
  ],
  completion: {
    title: 'Breadboard basics complete',
    message: 'You traced the key nets and built a valid resistor-and-LED circuit on a breadboard.',
    articleUrl: '/guides/how-to-use-a-breadboard',
  },
};

export default breadboardBasicsLesson;
